import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import ImageUploader from "./ImageUploader";
import {
  Check, ChevronRight, ChevronLeft, Loader2, Building, Phone, Clock, Share2,
  Search, Users, Sparkles, PartyPopper, Plus, Trash2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const ICON_MAP = { Building, Phone, Clock, Share2, Search, Users, Sparkles };
const DAYS = [
  { key: "mon", label: "Lun" }, { key: "tue", label: "Mar" }, { key: "wed", label: "Mer" },
  { key: "thu", label: "Jeu" }, { key: "fri", label: "Ven" }, { key: "sat", label: "Sam" }, { key: "sun", label: "Dim" },
];

export default function OnboardingPage() {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [team, setTeam] = useState([]);
  const [current, setCurrent] = useState(0);
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);

  const load = async () => {
    const [st, se, tm] = await Promise.all([
      api.get("/admin/onboarding/status"),
      api.get("/settings"),
      api.get("/admin/team").catch(() => ({ data: [] })),
    ]);
    setStatus(st.data);
    setSettings(se.data);
    setTeam(tm.data || []);
    if (st.data.completed_at) setCompleted(true);
  };
  useEffect(() => { load(); }, []);

  if (!status || !settings) return <div className="p-10 text-sm text-[hsl(var(--muted-foreground))]">Chargement…</div>;

  const steps = status.steps;
  const step = steps[current];
  const progress = status.percent;

  const saveSettings = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { ...settings, ...patch });
      setSettings(data);
      // refresh status
      const st = await api.get("/admin/onboarding/status");
      setStatus(st.data);
      return data;
    } finally { setSaving(false); }
  };

  const goNext = async (patch = {}) => {
    if (Object.keys(patch).length > 0) {
      await saveSettings(patch);
    }
    if (current < steps.length - 1) setCurrent(current + 1);
  };

  const finalize = async () => {
    try {
      setSaving(true);
      await api.post("/admin/onboarding/ack-widgets");
      await api.post("/admin/onboarding/finalize");
      toast.success("Profil complété à 100% !");
      setCompleted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const dismiss = async () => {
    await api.put("/admin/onboarding/dismiss", { dismissed: true });
    nav("/admin/dashboard");
  };

  if (completed) {
    return (
      <div className="p-10 flex items-center justify-center min-h-[70vh]">
        <Card className="max-w-md w-full p-10 text-center" data-testid="onboarding-completed">
          <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-5">
            <PartyPopper className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="font-display text-2xl font-bold text-[color:var(--brand-navy)]">Félicitations !</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">
            Votre site est entièrement configuré à 100%. L'équipe de votre agence a été notifiée et peut passer votre site en production.
          </p>
          <Button onClick={() => nav("/admin/dashboard")} className="mt-6 bg-[color:var(--brand-navy)]" data-testid="go-to-dashboard">
            Accéder au tableau de bord
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-10 max-w-4xl mx-auto" data-testid="onboarding-page">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">Bienvenue</div>
        <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Configurons votre site ensemble</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Suivez les étapes. Vous pourrez tout modifier plus tard.</p>
      </header>

      {/* Progress bar */}
      <Card className="p-4 mb-4 sticky top-2 z-10" data-testid="onboarding-progress">
        <div className="flex items-center justify-between mb-2">
          <span className="font-bold text-sm text-[color:var(--brand-navy)]">Progression : {progress}%</span>
          <span className="text-xs text-[hsl(var(--muted-foreground))]">{status.filled_required} / {status.total_required} champs</span>
        </div>
        <div className="h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[color:var(--brand-sky)] to-emerald-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {steps.map((s, i) => {
            const Icon = ICON_MAP[s.icon] || Sparkles;
            const isActive = i === current;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setCurrent(i)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${
                  isActive ? "bg-[color:var(--brand-navy)] text-white" :
                  s.done ? "bg-emerald-100 text-emerald-700" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
                }`}
                data-testid={`step-chip-${s.id}`}
              >
                {s.done ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                {s.label}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Step body */}
      <Card className="p-6 md:p-8 space-y-4" data-testid={`onboarding-step-${step.id}`}>
        <StepContent
          step={step}
          settings={settings}
          team={team}
          saving={saving}
          onChange={(patch) => setSettings(s => ({ ...s, ...patch }))}
          onTeamChange={setTeam}
          saveSettings={saveSettings}
        />

        <div className="flex items-center justify-between pt-4 border-t">
          <Button variant="ghost" onClick={() => setCurrent(Math.max(0, current - 1))} disabled={current === 0}>
            <ChevronLeft className="w-4 h-4 mr-1" />Précédent
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={dismiss} className="text-[hsl(var(--muted-foreground))] text-xs" data-testid="onboarding-later">
              Plus tard
            </Button>
            {current < steps.length - 1 ? (
              <Button onClick={() => goNext(settings)} disabled={saving} className="bg-[color:var(--brand-navy)]" data-testid="onboarding-next">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Suivant<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={finalize} disabled={saving || progress < 100} className="bg-gradient-to-br from-emerald-500 to-[color:var(--brand-navy)]" data-testid="onboarding-finalize">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                {progress < 100 ? `Terminer (encore ${100 - progress}%)` : "Finaliser mon profil"}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {step.missing.length > 0 && (
        <div className="mt-3 px-4 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Champs requis manquants : </strong>{step.missing.join(", ")}
          </div>
        </div>
      )}
    </div>
  );
}

// ----- Step content components -----
function StepContent({ step, settings, saving, onChange, team, onTeamChange, saveSettings }) {
  switch (step.id) {
    case "identity": return (
      <>
        <div><Label>Nom de l'entreprise *</Label><Input value={settings.company_name || ""} onChange={e => onChange({ company_name: e.target.value })} data-testid="ob-company-name" /></div>
        <div><Label>Nom court</Label><Input value={settings.company_short_name || ""} onChange={e => onChange({ company_short_name: e.target.value })} placeholder="Ex: MSC, KAP, Maison X" /></div>
        <div><Label>Slogan / Tagline</Label><Input value={settings.tagline || ""} onChange={e => onChange({ tagline: e.target.value })} placeholder="Une phrase qui résume votre valeur" /></div>
        <div><Label>Logo *</Label><ImageUploader value={settings.logo_url || ""} onChange={v => onChange({ logo_url: v })} /></div>
      </>
    );
    case "contact": return (
      <>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Téléphone *</Label><Input value={settings.phone || ""} onChange={e => onChange({ phone: e.target.value })} data-testid="ob-phone" /></div>
          <div><Label>Email *</Label><Input type="email" value={settings.email || ""} onChange={e => onChange({ email: e.target.value })} data-testid="ob-email" /></div>
        </div>
        <div><Label>Adresse publique *</Label><Input value={settings.address_public || ""} onChange={e => onChange({ address_public: e.target.value })} placeholder="Ville, code postal" /></div>
        <div><Label>Siège social (complet)</Label><Textarea rows={2} value={settings.address_headquarters || ""} onChange={e => onChange({ address_headquarters: e.target.value })} placeholder="Adresse complète du siège" /></div>
      </>
    );
    case "hours": return (
      <HoursEditor hours={settings.hours || []} onChange={v => onChange({ hours: v })} />
    );
    case "social": return (
      <>
        <div><Label>Facebook</Label><Input value={settings.facebook_url || ""} onChange={e => onChange({ facebook_url: e.target.value })} placeholder="https://facebook.com/…" /></div>
        <div><Label>Instagram</Label><Input value={settings.instagram_url || ""} onChange={e => onChange({ instagram_url: e.target.value })} placeholder="https://instagram.com/…" /></div>
        <div><Label>LinkedIn</Label><Input value={settings.linkedin_url || ""} onChange={e => onChange({ linkedin_url: e.target.value })} placeholder="https://linkedin.com/company/…" /></div>
        <div><Label>Nom exact sur Google Business</Label><Input value={settings.google_business_query || ""} onChange={e => onChange({ google_business_query: e.target.value })} placeholder="Ex: Plomberie Martin Paris 75018" /></div>
      </>
    );
    case "seo": return (
      <>
        <div><Label>Titre meta du site *</Label><Input value={settings.meta_title || ""} onChange={e => onChange({ meta_title: e.target.value })} maxLength={70} placeholder="Ex: Plomberie Martin — Dépannage Paris 75018" /></div>
        <div><Label>Description meta *</Label><Textarea rows={3} value={settings.meta_description || ""} onChange={e => onChange({ meta_description: e.target.value })} maxLength={180} placeholder="Entre 120 et 160 caractères" /></div>
        <div><Label>Favicon (icône onglet navigateur)</Label><ImageUploader value={settings.favicon_url || ""} onChange={v => onChange({ favicon_url: v })} /></div>
      </>
    );
    case "team": return <TeamStep team={team} onTeamChange={onTeamChange} />;
    case "widgets": return <WidgetsStep />;
    default: return null;
  }
}

function HoursEditor({ hours, onChange }) {
  const byDay = useMemo(() => {
    const m = {};
    DAYS.forEach(d => { m[d.key] = []; });
    (hours || []).forEach(h => { if (m[h.day]) m[h.day].push(h); });
    return m;
  }, [hours]);

  const update = (newByDay) => {
    const flat = [];
    DAYS.forEach(d => { (newByDay[d.key] || []).forEach(h => flat.push({ ...h, day: d.key })); });
    onChange(flat);
  };

  const addSlot = (day) => {
    const copy = { ...byDay };
    copy[day] = [...(copy[day] || []), { day, open: "09:00", close: "18:00" }];
    update(copy);
  };

  return (
    <div className="space-y-3" data-testid="ob-hours-editor">
      <p className="text-xs text-[hsl(var(--muted-foreground))]">Ajoutez vos plages horaires. Laissez vide pour un jour fermé.</p>
      {DAYS.map(d => (
        <div key={d.key} className="flex items-start gap-3 py-2 border-b last:border-0">
          <div className="w-12 shrink-0 pt-2 font-semibold text-sm">{d.label}</div>
          <div className="flex-1 space-y-1.5">
            {(byDay[d.key] || []).length === 0 && <div className="text-xs italic text-[hsl(var(--muted-foreground))] py-1.5">Fermé</div>}
            {(byDay[d.key] || []).map((slot, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input type="time" value={slot.open} onChange={e => { const c = { ...byDay }; c[d.key][i] = { ...slot, open: e.target.value }; update(c); }} className="w-28 h-8 text-xs" />
                <span>→</span>
                <Input type="time" value={slot.close} onChange={e => { const c = { ...byDay }; c[d.key][i] = { ...slot, close: e.target.value }; update(c); }} className="w-28 h-8 text-xs" />
                <Button variant="ghost" size="sm" onClick={() => { const c = { ...byDay }; c[d.key].splice(i, 1); update(c); }} className="text-red-600 h-7 px-2"><Trash2 className="w-3 h-3" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => addSlot(d.key)} className="h-7 text-xs"><Plus className="w-3 h-3 mr-1" />Ajouter</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function TeamStep({ team, onTeamChange }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", role: "", avatar_url: "" });
  const add = async () => {
    if (!form.first_name || !form.role) { toast.error("Prénom et rôle requis"); return; }
    try {
      const { data } = await api.post("/admin/team", form);
      onTeamChange(t => [...t, data]);
      setForm({ first_name: "", last_name: "", role: "", avatar_url: "" });
      toast.success(`${form.first_name} ajouté à l'équipe`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  return (
    <>
      <p className="text-xs text-[hsl(var(--muted-foreground))]">Ajoutez au moins un membre de votre équipe. Vous pourrez en ajouter d'autres plus tard.</p>
      {team.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-2">
          {team.map(m => (
            <Card key={m.id} className="p-3 flex items-center gap-3">
              {m.avatar_url ? <img src={m.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 rounded-full bg-[color:var(--brand-sky)]/15 flex items-center justify-center text-xs font-bold">{(m.first_name?.[0] || "?").toUpperCase()}</div>}
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{m.first_name} {m.last_name}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{m.role}</div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Card className="p-4 space-y-2 bg-[color:var(--brand-sky)]/5">
        <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--brand-navy)]">Nouveau membre</div>
        <div className="grid md:grid-cols-2 gap-2">
          <Input placeholder="Prénom *" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="ob-team-first" />
          <Input placeholder="Nom" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
        </div>
        <Input placeholder="Rôle * (ex: Gérant, Technicien…)" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} data-testid="ob-team-role" />
        <ImageUploader value={form.avatar_url} onChange={v => setForm({ ...form, avatar_url: v })} />
        <Button onClick={add} size="sm" className="w-full bg-[color:var(--brand-navy)]" data-testid="ob-team-add"><Plus className="w-3.5 h-3.5 mr-1" />Ajouter ce membre</Button>
      </Card>
    </>
  );
}

function WidgetsStep() {
  return (
    <>
      <p className="text-sm text-[hsl(var(--muted-foreground))]">
        Dernière étape : passez en revue les widgets que vous souhaitez activer pour votre site.
      </p>
      <Card className="p-4 bg-[color:var(--brand-sky)]/5 border-[color:var(--brand-sky)]/30">
        <Sparkles className="w-5 h-5 text-[color:var(--brand-sky)] mb-2" />
        <div className="font-bold text-[color:var(--brand-navy)] mb-1">Console des widgets</div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
          Ouvrez la console dans un autre onglet et activez les widgets qui vous concernent : Boutique, Réservation, Favoris, Popup rétention, RGPD…
        </p>
        <Button variant="outline" asChild data-testid="ob-widgets-link">
          <a href="/admin/widgets" target="_blank" rel="noopener noreferrer">Ouvrir la console des widgets →</a>
        </Button>
      </Card>
      <p className="text-xs text-[hsl(var(--muted-foreground))] italic">Une fois que vous avez fait votre sélection, cliquez sur <strong>Finaliser mon profil</strong> pour terminer l'onboarding.</p>
    </>
  );
}
